"use client";

import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CreditPlan, CreditPackId } from "@/lib/payments/plans";
import {
  FREE_PREVIEW_LIMIT,
  formatFreePreviewCountLabel,
} from "@/lib/payments/free-preview-config";
import {
  SHARED_PACK_INCLUDES,
  formatPriceUsd,
  getPackTagline,
} from "@/lib/payments/pricing-display";

type CreditPricingGridProps = {
  plans: CreditPlan[];
  isAuthenticated: boolean;
  hasCredits: boolean;
  canUseFreePreview: boolean;
  processingPackId: CreditPackId | null;
  onPurchasePaidPack: (packId: CreditPackId) => void;
  onFreePreviewClick?: () => void;
};

function getGridClass(planCount: number): string {
  if (planCount === 5) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5";
  if (planCount === 4) return "grid-cols-1 md:grid-cols-2 xl:grid-cols-4";
  if (planCount >= 6) return "grid-cols-1 md:grid-cols-3 xl:grid-cols-6";
  return "grid-cols-1 md:grid-cols-3";
}

function getPlanItemClass(index: number, planCount: number): string {
  if (planCount === 5 && index === 4) {
    return "sm:col-span-2 sm:max-w-md sm:mx-auto sm:w-full lg:col-span-1 lg:max-w-none lg:mx-0";
  }
  return "";
}

function getCtaLabel(
  plan: CreditPlan,
  isAuthenticated: boolean,
  hasCredits: boolean
): string {
  if (plan.isFree) {
    return isAuthenticated ? "Use free preview" : "Start for free";
  }
  if (hasCredits) return "Top up";
  return `Get ${plan.credits} previews`;
}

function getBadgeLabel(plan: CreditPlan): string | null {
  if (plan.hasPromo) return "Limited time";
  if (plan.popular) return "Most popular";
  return plan.badge ?? null;
}

function getBadgeClass(plan: CreditPlan): string {
  if (plan.hasPromo) {
    return "bg-amber-500/20 text-amber-100";
  }
  if (plan.popular) {
    return "bg-white text-[#0A0C0F]";
  }
  return "bg-emerald-500/15 text-emerald-200";
}

export function CreditPricingGrid({
  plans,
  isAuthenticated,
  hasCredits,
  canUseFreePreview,
  processingPackId,
  onPurchasePaidPack,
  onFreePreviewClick,
}: CreditPricingGridProps) {
  const router = useRouter();

  const handlePlanClick = (plan: CreditPlan) => {
    if (plan.isFree) {
      if (!isAuthenticated) {
        if (onFreePreviewClick) {
          onFreePreviewClick();
          return;
        }
        router.push("/app");
        return;
      }
      router.push("/app");
      return;
    }
    onPurchasePaidPack(plan.id as CreditPackId);
  };

  const paidPlans = plans.filter((p) => !p.isFree);

  return (
    <div className="space-y-6">
      <div
        className={cn(
          "grid items-stretch gap-5",
          getGridClass(plans.length)
        )}
      >
        {plans.map((plan, index) => {
          const isProcessing = processingPackId === plan.id;
          const isPopular = plan.popular;
          const priceParts = formatPriceUsd(plan.priceUsd);
          const tagline = getPackTagline(plan.id, plan.description);
          const badgeLabel = getBadgeLabel(plan);

          return (
            <article
              key={plan.id}
              className={cn(
                "flex h-full flex-col rounded-2xl border p-5",
                getPlanItemClass(index, plans.length),
                isPopular
                  ? "border-[#4A5568] bg-[#1C2230] shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
                  : "border-[#2D3548] bg-[#181C26]"
              )}
            >
              {/* Fixed badge row — keeps all cards aligned */}
              <div className="mb-4 flex h-6 items-center">
                {badgeLabel && (
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                      getBadgeClass(plan)
                    )}
                  >
                    {badgeLabel}
                  </span>
                )}
              </div>

              {/* Fixed name + tagline block */}
              <div className="mb-6 min-h-17">
                <h3 className="text-[15px] font-semibold leading-tight text-[#F3F4F6]">
                  {plan.name}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-[13px] leading-snug text-[#9CA3AF]">
                  {tagline}
                </p>
              </div>

              {/* Fixed price row */}
              <div className="mb-1 flex h-10 items-end">
                {plan.isFree ? (
                  <span className="text-[2rem] font-bold leading-none tracking-tight text-[#F3F4F6]">
                    $0
                  </span>
                ) : (
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-[2rem] font-bold leading-none tracking-tight text-[#F3F4F6]">
                      ${priceParts.dollars}
                    </span>
                    <span className="pb-0.5 text-base font-medium text-[#9CA3AF]">
                      .{priceParts.cents}
                    </span>
                  </div>
                )}
              </div>

              {/* Fixed credits row */}
              <div className="mb-8 h-5">
                <p className="text-sm font-medium text-[#C4CAD4]">
                  {plan.isFree
                    ? formatFreePreviewCountLabel(FREE_PREVIEW_LIMIT)
                    : `${plan.credits} HD previews`}
                </p>
              </div>

              {/* CTA pinned to bottom */}
              <div className="mt-auto">
                {!(plan.isFree && !canUseFreePreview) && (
                  <Button
                    className={cn(
                      "h-11 w-full text-sm font-semibold",
                      isPopular
                        ? "bg-white text-[#0A0C0F] hover:bg-[#E8EAED]"
                        : "border border-[#3D4659] bg-[#222938] text-[#F3F4F6] hover:bg-[#2A3140]"
                    )}
                    variant={isPopular ? "default" : "ghost"}
                    onClick={() => handlePlanClick(plan)}
                    disabled={!plan.isFree && processingPackId !== null}
                  >
                    {plan.isFree ? (
                      <>
                        {getCtaLabel(plan, isAuthenticated, hasCredits)}
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </>
                    ) : isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        {getCtaLabel(plan, isAuthenticated, hasCredits)}
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {paidPlans.length > 0 && (
        <div className="rounded-2xl border border-[#2D3548] bg-[#181C26] px-6 py-5">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
            Every pack includes
          </p>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {SHARED_PACK_INCLUDES.map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm text-[#D1D5DB]">
                <Check className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
